import React from "react";
import { Box, Typography, Avatar, AvatarGroup, Divider } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import GroupIcon from "@mui/icons-material/Group";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CommonButton from "../../ui/inputs/CommonButton";
import colors from "../../../../theme/colors";
import { borderRadiusPx } from "../../../../theme/borderRadius";
import { useTheme } from "@mui/material/styles";
import { trustShineSx } from "../index.js";

const GlobalCommunityUI = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        mx: "auto",
        maxWidth: "1400px",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center" },
        px: theme.custom["home-section-left-padding"],
        pt: theme.custom.layoutSizes.section.paddingTop,
        pb: theme.custom.layoutSizes.section.paddingBottom,
        boxSizing: "border-box",
        zIndex: 10,
        pointerEvents: "none",
      }}
    >

      {/* LEFT SIDE CONTENT */}
      <Box 
        sx={{ 
          maxWidth: { xs: "100%", md: "500px" }, 
          pointerEvents: "auto",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          zIndex: 2,
        }}
      >
        <Box sx={(theme) => theme.typography.homeSection?.headerContainer || {}}>
          <Typography
            component="p"
            sx={{
              ...(theme.typography.homeSection?.eyebrow || {}),
              color: colors.primary.main,
              textShadow: "0 2px 10px rgba(0,0,0,0.5)",
              "&::before": {
                ...(theme.typography.homeSection?.eyebrow?.["&::before"] || {}),
                background: colors.primary.main,
              },
            }}
          >
            GLOBAL COMMUNITY
          </Typography>

          <Typography
            component="h2"
            sx={{
              ...(theme.typography.homeSection?.heading || {}),
              color: colors.neutral.white,
              textShadow: "0 4px 20px rgba(0,0,0,0.6)",
              mb: { xs: 2, md: 3 },
            }}
          >
            Played in <br />
            <Box component="span" sx={{ ...trustShineSx(colors.primary.light), fontSize: { xs: "2.5rem", md: "3.5rem" }, lineHeight: 1, display: "inline-block" }}>150+</Box><br />
            Countries
          </Typography>

          <Typography
            component="p"
            sx={{
              ...(theme.typography.homeSection?.description || {}),
              color: colors.alpha.white[80],
              maxWidth: { xs: "280px", md: "450px" },
              textShadow: "0 2px 10px rgba(0,0,0,0.5)",
              mb: { xs: 3, md: 5 },
            }}
          >
            Join a growing community of players worldwide. AutoBET is borderless,
            accessible and built for everyone.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: { xs: "flex-start", md: "center" }, flexDirection: { xs: "column", md: "row" }, gap: { xs: 2.5, md: 4 } }}>
          <CommonButton variant="primary" size="large" sx={{ borderRadius: borderRadiusPx.xl, px: 4, py: 1.5, fontSize: "1rem" }}>
            Join Now
          </CommonButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AvatarGroup 
              max={4} 
              sx={{ 
                "& .MuiAvatar-root": { 
                  width: { xs: 32, md: 36 }, 
                  height: { xs: 32, md: 36 }, 
                  borderColor: colors.home.navyDeep,
                  borderWidth: 2
                } 
              }}
            >
              <Avatar sx={{ bgcolor: colors.primary.light }} />
              <Avatar sx={{ bgcolor: colors.error.main }} />
              <Avatar sx={{ bgcolor: colors.success.main }} />
              <Avatar sx={{ bgcolor: colors.purple[500] }} />
            </AvatarGroup>

            <Typography sx={{ fontSize: "0.85rem", color: colors.alpha.white[70], fontWeight: 600, textShadow: "0 2px 10px rgba(0,0,0,0.5)" }}>
              +482K Players
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* RIGHT SIDE STATS (Becomes bottom pill box on mobile) */}
      <Box 
        sx={{ 
          display: "flex", 
          flexDirection: { xs: "row", md: "column" }, 
          gap: { xs: 0, md: 4 }, 
          pointerEvents: "auto",
          width: { xs: "100%", md: "auto" },
          mt: { xs: "auto", md: 0 },
          justifyContent: { xs: "space-between", md: "flex-start" },
          backgroundColor: { xs: "rgba(5, 15, 25, 0.4)", md: "transparent" },
          backdropFilter: { xs: "blur(12px)", md: "none" },
          border: { xs: "1px solid rgba(255,255,255,0.08)", md: "none" },
          borderRadius: { xs: "16px", md: "0" },
          p: { xs: "20px 10px", md: 0 },
          position: "relative",
          zIndex: 2,
        }}
      >
        
        {/* Stat 1 */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: { xs: 1.2, md: 2.5 }, flex: 1, textAlign: "center" }}>
          <Box
            sx={{
              width: { xs: 44, md: 56 },
              height: { xs: 44, md: 56 },
              borderRadius: borderRadiusPx.circle,
              backgroundColor: colors.alpha.white[5],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(10px)",
            }}
          >
            <LanguageIcon sx={{ color: colors.alpha.white[80], fontSize: { xs: 22, md: 28 } }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.5rem" }, fontWeight: 800, color: colors.neutral.white, lineHeight: 1.2 }}>
              150+
            </Typography>
            <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.85rem" }, color: colors.alpha.white[60], fontWeight: 500, mt: 0.3 }}>
              Countries
            </Typography>
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: "block", md: "none" }, borderColor: "rgba(255,255,255,0.1)", height: "auto", my: 1 }} />

        {/* Stat 2 */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: { xs: 1.2, md: 2.5 }, flex: 1, textAlign: "center" }}>
          <Box
            sx={{
              width: { xs: 44, md: 56 },
              height: { xs: 44, md: 56 },
              borderRadius: borderRadiusPx.circle,
              backgroundColor: colors.alpha.white[5],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(10px)",
            }}
          >
            <GroupIcon sx={{ color: colors.alpha.white[80], fontSize: { xs: 22, md: 28 } }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.5rem" }, fontWeight: 800, color: colors.neutral.white, lineHeight: 1.2 }}>
              482K+
            </Typography>
            <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.85rem" }, color: colors.alpha.white[60], fontWeight: 500, mt: 0.3 }}>
              Players
            </Typography>
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: "block", md: "none" }, borderColor: "rgba(255,255,255,0.1)", height: "auto", my: 1 }} />

        {/* Stat 3 */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: { xs: 1.2, md: 2.5 }, flex: 1, textAlign: "center" }}>
          <Box
            sx={{
              width: { xs: 44, md: 56 },
              height: { xs: 44, md: 56 },
              borderRadius: borderRadiusPx.circle,
              backgroundColor: colors.alpha.white[5],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(10px)",
            }}
          >
            <EmojiEventsIcon sx={{ color: colors.alpha.white[80], fontSize: { xs: 22, md: 28 } }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: "1.2rem", md: "1.5rem" }, fontWeight: 800, color: colors.neutral.white, lineHeight: 1.2 }}>
              $24.9M+
            </Typography>
            <Typography sx={{ fontSize: { xs: "0.75rem", md: "0.85rem" }, color: colors.alpha.white[60], fontWeight: 500, mt: 0.3 }}>
              Total Winnings
            </Typography>
          </Box>
        </Box>

      </Box>

      {/* MOBILE BOTTOM TEXT */}
      <Box
        sx={{
          position: "absolute",
          bottom: 25,
          left: 0,
          width: "100%",
          display: { xs: "flex", md: "none" },
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
          zIndex: 1,
        }}
      >
        <Box sx={{ width: 30, height: "1px", bgcolor: "rgba(255,255,255,0.15)" }} />
        <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.55rem", letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 700 }}>
          A Global Gaming Community
        </Typography>
        <Box sx={{ width: 30, height: "1px", bgcolor: "rgba(255,255,255,0.15)" }} />
      </Box>
    </Box>
  );
};

export default GlobalCommunityUI;